import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import CustomizedButton from 'src/components/WidgetMaker/WidgetDnD/WIdgetPropertiesBasic/components/Button';
import { getThemeColor } from 'src/utils/getThemeColor';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ReviewMediaRendered } from '../Common';
import { IS_MOBILE } from 'src/utils/checkClientDeviceType';

interface IProductReviewMedia {
  reviews: any[];
  isLoading: boolean;
  handleProductReviewModalOpen: (reviewIndex: number, mediaIndex: number) => void;
  canLoadMore: boolean;
  onLoadMore: () => void;
}

export function ProductReviewMedia(props: IProductReviewMedia) {
  const { reviews, isLoading, handleProductReviewModalOpen, canLoadMore, onLoadMore } =
    props;

  const scrollRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScrollButtons();
    el.addEventListener('scroll', checkScrollButtons);
    window.addEventListener('resize', checkScrollButtons);

    return () => {
      el.removeEventListener('scroll', checkScrollButtons);
      window.removeEventListener('resize', checkScrollButtons);
    };
  }, []);

  const checkScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollable = el.scrollWidth > el.clientWidth;
    setIsScrollable(scrollable);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const scrollByAmount = (amount) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  if (!reviews?.length) {
    return null;
  }

  const themeColor = getThemeColor();

  return (
    (isLoading || !!reviews?.length) && (
      <div className="tw-mt-[40px] tw-flex tw-flex-col tw-gap-[16px]">
        {
          <div className="tw-flex tw-justify-between">
            <p className="tw-m-0">Customer photos & videos</p>
            {!IS_MOBILE && isScrollable && (
              <span className="tw-flex tw-gap-[2px]">
                <span
                  onClick={() => scrollByAmount(-150)}
                  className={classNames('tw-flex', !canScrollLeft ? 'tw-opacity-30' : '')}
                >
                  <ArrowBackIosIcon fontSize="small" className="!tw-p-0" />
                </span>
                <span
                  onClick={() => scrollByAmount(150)}
                  className={classNames(
                    'tw-flex',
                    !canScrollRight ? 'tw-opacity-30' : ''
                  )}
                >
                  <ArrowForwardIosIcon fontSize="small" className="!tw-p-0" />
                </span>
              </span>
            )}
          </div>
        }
        <div
          className="tw-flex tw-h-[110px] tw-items-center tw-gap-[16px] tw-overflow-scroll tw-scroll-smooth"
          ref={scrollRef}
        >
          {isLoading ? (
            <div className="tw-h-full tw-w-full tw-animate-pulse tw-bg-slate-200" />
          ) : (
            reviews?.map((review, index) => {
              return review?.store_item_review_media?.map((media, idx) => (
                <ReviewMediaRendered
                  key={idx}
                  media={media}
                  onClick={() => {
                    handleProductReviewModalOpen(index, idx);
                  }}
                  size={IS_MOBILE ? 104 : 110}
                />
              ));
            })
          )}
          {canLoadMore && (
            <CustomizedButton
              className="!tw-h-fit !tw-w-fit tw-cursor-pointer tw-text-nowrap !tw-rounded-full !tw-px-[16px] !tw-py-[2px] tw-text-white"
              backgroundColor={themeColor}
              onClick={onLoadMore}
            >
              More
            </CustomizedButton>
          )}
        </div>
      </div>
    )
  );
}
